import { loadComposeVoices } from "@mirage/service-compose";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import type {
  ComposeVoice,
  DefaultComposeVoice,
} from "@mirage/shared/compose/compose-voice";
import { createDefaultTones } from "@mirage/shared/compose/compose-voice";
import * as rx from "rxjs";
import { v5 as uuidv5 } from "uuid";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";
import { getResultSection } from "../utils/section-mapper";
import { getSourceLimit } from "../utils/source-helpers";

const FILTER_RESULT_LIMIT = 20;

const DRAFT_FROM_TONE_UUID_NAMESPACE = "de19e086-03f2-11ef-bbd8-425096b39f42";

type ToneSource = ComposeVoice | DefaultComposeVoice;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { cache, config, grossLimit, sectionLimit },
) => {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.DraftFromTone,
    query,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.DraftFromTone,
    mode,
    query,
    config,
  );

  return rx.defer(() => {
    if (mode === typeahead.Mode.Default && query.length > 0) {
      return rx.of();
    }

    const staticTones = createDefaultTones();

    return rx.from(loadComposeVoices()).pipe(
      rx.map((cachedCustomTones) => {
        const deduplicatedTones = deduplicateTones([
          ...staticTones,
          ...cachedCustomTones,
        ]);

        const sources = twoStageSearch<ToneSource>(
          query,
          deduplicatedTones.map((tone) => ({
            searchableStrings: [tone.name, tone.description],
            originalResult: tone,
          })),
          { limit: FILTER_RESULT_LIMIT },
        ).map((searchableResult) => searchableResult.originalResult);

        return sources
          .map((tone) => {
            const taggedResult = primitives.draftFromTone(
              tone.id,
              {
                uuid: uuidv5(tone.id, DRAFT_FROM_TONE_UUID_NAMESPACE),
                voiceId: tone.id,
                title: tone.name,
              },
              {
                hits: cache.getHits(tone.id),
                sectionId,
              },
            );

            return sourceScore.score(query, config)(taggedResult);
          })
          .sort((a, b) => b.score - a.score)
          .slice(0, sourceLimit);
      }),
      rx.mergeMap((results) => rx.from(results)),
    );
  });
};

export function deduplicateTones(tones: ToneSource[]): ToneSource[] {
  return tones.filter(
    (tone, index, self) => index === self.findIndex((t) => t.id === tone.id),
  );
}

export const search = wrappers.wrapped(SourceId.DraftFromTone, raw);
